/**
 * Notice: a standing message the reader must see. The KIND is a
 * paint from the meaning map, never a free colour: absence amber for
 * a warning, violating red for a failure, complying green for a
 * success, secondary ink for information.
 */
import type { ReactNode } from 'react';
import { RADIUS, SPACE, TEXT, WASH_ALPHA, WEIGHT, tint, usePalette, type Hex, type Palette } from '../theme';

export type NoticeKind = 'info' | 'warning' | 'error' | 'success';

/** The meaning-map paint a notice of this kind wears. */
function noticePaint(p: Palette, kind: NoticeKind): Hex {
  switch (kind) {
    case 'info':
      return p.inkSecondary;
    case 'warning':
      return p.absence;
    case 'error':
      return p.violating;
    case 'success':
      return p.complying;
  }
}

export function Notice({
  kind = 'info',
  title,
  children,
  actions,
}: {
  kind?: NoticeKind;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const p = usePalette();
  const paint = noticePaint(p, kind);
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: SPACE.md,
        padding: `${SPACE.snug}px ${SPACE.md}px`,
        borderRadius: RADIUS.md,
        border: `1px solid ${tint(paint, WASH_ALPHA.noticeEdge)}`,
        background: tint(paint, WASH_ALPHA.fill),
        color: p.ink,
        fontSize: TEXT.control,
        lineHeight: 1.45,
      }}
    >
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: SPACE.xxs }}>
        {title && <div style={{ fontWeight: WEIGHT.strong, color: paint }}>{title}</div>}
        {children && <div style={{ wordBreak: 'break-word' }}>{children}</div>}
      </div>
      {actions && <div style={{ flexShrink: 0, display: 'flex', gap: SPACE.sm }}>{actions}</div>}
    </div>
  );
}
